/**
 * 🚀 UNIVERSAL BUTTON ELEMENT 2025
 * 
 * Dynamisches Button-Element für HeroHeader
 * - Voll responsive (Desktop/Mobile)
 * - Alle Button-Styling-Optionen
 * - Gradient, Shadow, Border Support
 * - Animations Support
 */

import React from 'react';
import EditableButton from './EditableButton';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalButtonElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    fonts = {}, // ✅ Fonts für globalSmallFont Fallback
    textAlignment = 'left', // ✅ DEPRECATED - Nur als Fallback wenn buttonAlignment nicht gesetzt
    isCurrentlyEditing = false, // ✅ NEW: Ob dieser Button gerade editiert wird
    onEditingStart = () => {}, // ✅ NEW: Callback wenn Editing startet
    onEditingEnd = () => {} // ✅ NEW: Callback wenn Editing endet
}) {
    // ✅ Alignment für Button basierend auf buttonAlignment (responsive)
    // Falls buttonAlignment nicht gesetzt, nutze textAlignment als Fallback
    const activeAlignment = isMobileLayout 
        ? (element.buttonAlignmentMobile || element.buttonAlignment || textAlignment)
        : (element.buttonAlignment || textAlignment);
    
    const getAlignSelf = () => {
        if (activeAlignment === 'center') return 'center';
        if (activeAlignment === 'right') return 'flex-end';
        return 'flex-start';
    };

    return (
        <div style={{
            width: '100%', // ✅ WICHTIG: Volle Breite damit 100% Button funktioniert
            display: 'flex', // ✅ WICHTIG: Flex-Container für Button Alignment
            flexDirection: 'column', // ✅ Column für korrekte Stacking
            marginTop: appendPx(isMobileLayout ? (element.marginTopMobile ?? element.marginTop ?? 0) : (element.marginTop ?? 0)), // ✅ Fallback auf 0
            marginBottom: appendPx(isMobileLayout ? (element.marginBottomMobile ?? element.marginBottom ?? 0) : (element.marginBottom ?? 0)),
            marginLeft: appendPx(isMobileLayout ? (element.marginLeftMobile ?? element.marginLeft ?? 0) : (element.marginLeft ?? 0)),
            marginRight: appendPx(isMobileLayout ? (element.marginRightMobile ?? element.marginRight ?? 0) : (element.marginRight ?? 0))
        }}>
            <EditableButton
                // Required Props
                element={element} // ✅ NEW: Element Objekt für Custom Event
                buttonText={element.buttonText || ''}
                onTextChange={(newText) => {
                    if (onUpdate) {
                        onUpdate({ ...element, buttonText: newText });
                    }
                }}
                isEditMode={isEditMode}
                isSelected={isCurrentlyEditing} // ✅ FIXED: Nutzt currentlyEditingElementId System!
                onSelect={() => {
                    onEditingStart(); // ✅ Notify parent dass dieser Button editiert wird
                    if (onSelect) onSelect();
                }}
                onDeselect={() => {
                    onEditingEnd(); // ✅ Notify parent dass Editing endet
                }}
                
                // Style Props
                buttonColor={element.buttonColor || '#5C80BC'}
                buttonTextColor={element.buttonTextColor || '#ffffff'}
                buttonTextSize={element.buttonTextSize || 18}
                buttonTextSizeMobile={element.buttonTextSizeMobile || 16}
                buttonTextWeight={element.buttonTextWeight || '600'}
                buttonTextWeightMobile={element.buttonTextWeightMobile || '600'}
                buttonFontFamily={element.buttonFontFamily || fonts.globalSmallFont} // ✅ FIXED: Font Family hinzugefügt!
                buttonBorderRadius={element.buttonBorderRadius ?? 8}
                buttonBorderRadiusMobile={element.buttonBorderRadiusMobile ?? 8}
                buttonHeight={element.buttonHeight || 50}
                buttonHeightMobile={element.buttonHeightMobile || 48}
                buttonWidth={element.buttonWidth || 200}
                buttonWidthMobile={element.buttonWidthMobile || 100}
                buttonMaxWidth={element.buttonMaxWidth || 300}
                buttonMaxWidthMobile={element.buttonMaxWidthMobile || 280}
                
                // Advanced Props
                buttonBorderEnabled={element.buttonBorderEnabled || false}
                buttonBorderWidth={element.buttonBorderWidth || 2}
                buttonBorderColor={element.buttonBorderColor || element.buttonColor || '#5C80BC'}
                buttonShadowEnabled={element.buttonShadowEnabled || false}
                buttonShadowIntensity={element.buttonShadowIntensity || 15}
                buttonShadowDistance={element.buttonShadowDistance || 8}
                buttonShadowOpacity={element.buttonShadowOpacity || 25}
                buttonShadowColor={element.buttonShadowColor || '#000000'}
                buttonGradientEnabled={element.buttonGradientEnabled || false}
                buttonGradientColor1={element.buttonGradientColor1 || '#667eea'}
                buttonGradientColor2={element.buttonGradientColor2 || '#764ba2'}
                buttonGradientAnimation={element.buttonGradientAnimation || false}
                buttonArrowEnabled={element.buttonArrowEnabled || false}
                buttonArrowDirection={element.buttonArrowDirection || 'left'}
                buttonArrowIcon={element.buttonArrowIcon || 'arrow_1'} // ✅ NEW
                buttonArrowSize={element.buttonArrowSize || 24} // ✅ NEW
                buttonArrowInvert={element.buttonArrowInvert || false} // ✅ NEW
                
                // Animation Props
                buttonAnimationEnabled={element.buttonAnimationEnabled || false}
                buttonAnimationType={element.buttonAnimationType || 'pulse'}
                
                // ✅ MARGINS - Wichtig für ButtonFloatingToolbar!
                buttonMarginTop={isMobileLayout ? (element.marginTopMobile ?? element.marginTop ?? 0) : (element.marginTop ?? 0)}
                buttonMarginBottom={isMobileLayout ? (element.marginBottomMobile ?? element.marginBottom ?? 0) : (element.marginBottom ?? 0)}
                
                // ✅ ALIGNMENT - Wichtig für ButtonFloatingToolbar!
                buttonAlignment={element.buttonAlignment || 'left'}
                buttonAlignmentMobile={element.buttonAlignmentMobile || 'left'}
                
                // Callbacks
                onUpdate={(updates) => {
                    console.log('🔄 [UniversalButtonElement] onUpdate called:', {
                        updates,
                        elementId: element.id,
                        hasParentOnUpdate: !!onUpdate
                    });
                    
                    if (!onUpdate) {
                        console.error('❌ [UniversalButtonElement] Parent onUpdate is undefined!');
                        return;
                    }
                    
                    // ✅ Map buttonMarginTop/Bottom zurück zu marginTop/Bottom
                    const mappedUpdates = { ...updates };
                    if (updates.buttonMarginTop !== undefined) {
                        if (isMobileLayout) {
                            mappedUpdates.marginTopMobile = updates.buttonMarginTop;
                        } else {
                            mappedUpdates.marginTop = updates.buttonMarginTop;
                        }
                        delete mappedUpdates.buttonMarginTop;
                    }
                    if (updates.buttonMarginBottom !== undefined) {
                        if (isMobileLayout) {
                            mappedUpdates.marginBottomMobile = updates.buttonMarginBottom;
                        } else {
                            mappedUpdates.marginBottom = updates.buttonMarginBottom;
                        }
                        delete mappedUpdates.buttonMarginBottom;
                    }
                    
                    // ✅ Map buttonAlignment zurück zu buttonAlignment (Desktop/Mobile)
                    if (updates.buttonAlignment !== undefined) {
                        if (isMobileLayout) {
                            mappedUpdates.buttonAlignmentMobile = updates.buttonAlignment;
                        } else {
                            mappedUpdates.buttonAlignment = updates.buttonAlignment;
                        }
                        // Lösche Original wenn es Mobile war
                        if (isMobileLayout) {
                            delete mappedUpdates.buttonAlignment;
                        }
                    }
                    
                    const updatedElement = { ...element, ...mappedUpdates };
                    console.log('✅ [UniversalButtonElement] Calling parent onUpdate with:', updatedElement);
                    onUpdate(updatedElement);
                    console.log('✅ [UniversalButtonElement] Parent onUpdate called');
                }}
                colors={colors}
                brandFonts={fonts} // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont) für Font Family Section
                
                // Additional Props
                placeholder="Enter button text..."
                buttonLink={element.buttonLink || ''}
                
                // Responsive
                isMobileLayout={isMobileLayout}
                
                // Styling
                alignSelf={getAlignSelf()}
                className="hero-button responsive-font"
            />
        </div>
    );
}

export default UniversalButtonElement;

